import * as Accordion from '@radix-ui/react-accordion'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { PageContainer } from '@/layout/pageContainer'
import { cn } from '@/libs/tw'
import { Step, useStepStore } from '@/stores/step-store/stepStore'

type StepProps = {
	step: Step
	title: string
	number: string
	instructions?: string
	children: JSX.Element
	summary: JSX.Element
}

export const BookingStep = ({
	step,
	title,
	number,
	instructions,
	children,
	summary,
}: StepProps) => {
	const summarySteps = useStepStore.use.summarySteps()
	const currentStep = useStepStore.use.currentStep()
	const setCurrentStep = useStepStore.use.setCurrentStep()

	const isInSummary = summarySteps.includes(step)
	const isCurrentStep = currentStep === step
	const showSummary = isInSummary && !isCurrentStep
	const isInactive = !isInSummary && !isCurrentStep

	return (
		<PageContainer
			className={cn(
				showSummary && 'border-b border-solid border-textColor bg-secondary',
				isCurrentStep && 'bg-primary',
				isInactive && 'border-b border-solid border-dark-light bg-naturel'
			)}
			id={step}
			separator={isInactive}
			largePadding={isCurrentStep}
		>
			<Accordion.Item value={step} className="w-full">
				<Accordion.Header className={cn('flex w-full flex-col')}>
					<div className={cn('flex w-full items-center justify-between text-[32px]')}>
						<div
							className={cn(
								'flex items-center gap-24 font-bookingStep md:gap-40',
								isInactive
									? 'text-bookingStep-text-inactive'
									: 'text-bookingStep-text-active'
							)}
						>
							<Typography
								style="h2"
								element="span"
								className={cn('font-normal', isInactive && 'opacity-50')}
							>
								{number}
							</Typography>
							<Typography style="h2" element="h2">
								{isCurrentStep ? (instructions ?? title) : title}
							</Typography>
						</div>
						{showSummary && (
							<button onClick={() => setCurrentStep(step)} name="edit">
								<SvgIcon
									collectionName="ibe3"
									iconName="edit"
									className="h-32 w-32 text-textColor"
								/>
							</button>
						)}
					</div>
					{showSummary && (
						<div
							aria-label="edit"
							className={cn('cursor-pointer pt-32')}
							onClick={() => setCurrentStep(step)}
						>
							{summary}
						</div>
					)}
				</Accordion.Header>
				<Accordion.Content className="w-full pt-32">{children}</Accordion.Content>
			</Accordion.Item>
		</PageContainer>
	)
}
